/**
 * @license
 * Copyright (C) 2025 Guido Berhoerster <guido+voxit@berhoerster.name>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import "@voxit/voxit-web-components";

import voxitFrontendContextHelpStyleSheet from "../style/vrf-contexthelp.css" with { type: "css" };
import voxitFrontendResultsStyleSheet from "../style/vrf-results.css" with { type: "css" };
import voxitFrontendSplitProgressStyleSheet from "../style/vrf-splitprogress.css" with { type: "css" };
import voxitFrontendStackedLayerStyleSheet from "../style/vrf-stackedlayer.css" with { type: "css" };
import voxitFrontendStackedStyleSheet from "../style/vrf-stacked.css" with { type: "css" };
import voxitFrontendStatementCountStyleSheet from "../style/vrf-statementcount.css" with { type: "css" };
import voxitFrontendTabPanelStyleSheet from "../style/vrf-tabpanel.css" with { type: "css" };
import voxitFrontendTabsStyleSheet from "../style/vrf-tabs.css" with { type: "css" };
import voxitFrontendTabStyleSheet from "../style/vrf-tab.css" with { type: "css" };
import voxitFrontendTooltipStyleSheet from "../style/vrf-tooltip.css" with { type: "css" };
import voxitFrontendFlashStyleSheet from "../style/vrf-flash.css" with { type: "css" };

class VoxitFrontendChangeEvent extends Event {
    constructor(type, options = {}) {
        super(type, { bubbles: true });
    }
}

function getStatementProgress(client) {
    let current = 1;
    let total = client.conversation.seedStatements.size;
    if (client.statements.size > 0) {
        const otherStatementIDs = new Set(client.statements.keys());
        for (const id of client.participant.statements) {
            otherStatementIDs.delete(id);
        }
        total = otherStatementIDs.size;
        for (const id of client.participant.votes) {
            if (!client.participant.statements.has(id) && client.statements.has(id)) {
                current++;
            }
        }
    }
    return { current, total };
}

function canReviseVote(conversationElement) {
    const previousStatement = conversationElement?._vrfPreviousStatement;
    return previousStatement !== null &&
        typeof previousStatement !== "undefined" &&
        conversationElement?.statement?.id !== previousStatement.id;
}

function isRevisingVote(conversationElement) {
    const previousStatement = conversationElement?._vrfPreviousStatement;
    return previousStatement !== null &&
        typeof previousStatement !== "undefined" &&
        conversationElement?.statement?.id === previousStatement.id;
}

function renderConversationStatement(conversationElement) {
    if (!(conversationElement instanceof HTMLElement)) {
        return;
    }
    const statementElement = conversationElement.querySelector("voxit-statement");
    if (statementElement === null) {
        return;
    }

    const statement = conversationElement.statement;
    if (statement !== null) {
        statementElement.text = statement.text;
        statementElement.isMeta = statement.isMeta;
        statementElement.noStatement = false;
    } else {
        statementElement.text = "";
        statementElement.isMeta = false;
        statementElement.noStatement = true;
    }
}

class VoxitFrontendReviseVoteButton extends HTMLElement {
    static observedAttributes = ["disabled"];
    #buttonElement;
    #conversationElement = null;
    #internals;
    #inactive = true;

    constructor() {
        super();

        const sheet = new CSSStyleSheet();
        sheet.replaceSync(`
            :host {
                display: inline;
            }
            :host([hidden]) {
                display: none;
            }
        `);
        this.#buttonElement = document.createElement("button");
        this.#buttonElement.type = "button";
        this.#buttonElement.part.add("button");
        this.#buttonElement.appendChild(document.createElement("slot"));
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [sheet];
        shadow.appendChild(this.#buttonElement);
        this.#internals = this.attachInternals();
        this.addEventListener("click", this);
    }

    connectedCallback() {
        this.#conversationElement = this.closest("voxit-conversation");
        this.#conversationElement?.addEventListener("voxitstatechange", this);
        this.#conversationElement?.addEventListener("voxitauthenticationchange", this);
        this.#conversationElement?.addEventListener("voxitauthenticationsuccess", this);
        this.#conversationElement?.addEventListener("voxitconversationchange", this);
        this.#conversationElement?.addEventListener("voxitvotesubmitsuccess", this);
        this.#conversationElement?.addEventListener("voxitrevisevote", this);
        this.#updateInactive();
    }

    disconnectedCallback() {
        this.#conversationElement?.removeEventListener("voxitstatechange", this);
        this.#conversationElement?.removeEventListener("voxitauthenticationchange", this);
        this.#conversationElement?.removeEventListener("voxitauthenticationsuccess", this);
        this.#conversationElement?.removeEventListener("voxitconversationchange", this);
        this.#conversationElement?.removeEventListener("voxitvotesubmitsuccess", this);
        this.#conversationElement?.removeEventListener("voxitrevisevote", this);
        this.#conversationElement = null;
    }

    attributeChangedCallback(name, oldValue, newValue) {
        switch (name) {
            case "disabled":
                this.#buttonElement.disabled = this.disabled || this.#inactive;
                break;
        }
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "click":
                if (this.#inactive || this.disabled) {
                    return;
                }
                this.dispatchEvent(new Event("voxitrevisevote", { bubbles: true }));
                break;
            default:
                this.#updateInactive();
                break;
        }
    }

    get disabled() {
        return this.hasAttribute("disabled");
    }

    set disabled(value) {
        this.toggleAttribute("disabled", Boolean(value));
    }

    #updateInactive() {
        const inactive = this.#conversationElement === null ||
            this.#conversationElement.state !== "loaded" ||
            this.#conversationElement.client.authenticationRequired ||
            !canReviseVote(this.#conversationElement);
        this.#inactive = inactive;
        this.#buttonElement.disabled = this.disabled || inactive;
        if (inactive) {
            this.#internals.states.add("inactive");
        } else {
            this.#internals.states.delete("inactive");
        }
    }
}
window.customElements.define("voxit-revise-vote-button", VoxitFrontendReviseVoteButton);

function createDescriptionParagraphs(value) {
    const normalizedValue = value.replace(/\r\n?/g, "\n");
    const paragraphs = normalizedValue.split(/\n\s*\n+/);
    return paragraphs.filter((paragraph) => paragraph.trim() !== "").map((paragraph) => {
        const paragraphElement = document.createElement("p");
        paragraphElement.className = "description-paragraph";
        paragraphElement.textContent = paragraph;
        return paragraphElement;
    });
}

function createDescriptionNodes(descriptionDocument, descriptionText) {
    if (descriptionDocument instanceof DocumentFragment) {
        return [...descriptionDocument.childNodes].flatMap((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
                const element = /** @type {Element} */ (node.cloneNode(true));
                if (element.tagName === "P") {
                    element.classList.add("description-paragraph");
                }
                return [element];
            }
            if (node.nodeType === Node.TEXT_NODE) {
                return node.textContent === null ? [] : createDescriptionParagraphs(node.textContent);
            }
            return [];
        });
    }
    return createDescriptionParagraphs(descriptionText ?? "");
}

class VoxitFrontendStatementTranslation extends HTMLElement {
    #conversationElement = null;
    #buttonElement;
    #cache = new Map();
    #abortController = null;
    #enabled = false;
    #loading = false;
    #translationText = "";
    #lastStatementID = null;
    #lastLanguage = "";
    #lastPolisBaseURL = "";

    constructor() {
        super();

        this.#buttonElement = document.createElement("button");
        this.#buttonElement.type = "button";
        this.#buttonElement.className = "link-button statement-translation-toggle";
        this.append(this.#buttonElement);
    }

    connectedCallback() {
        this.#conversationElement = this.closest("voxit-conversation");
        if (this.#conversationElement === null) {
            this.hidden = true;
            console.error("vrf-statement-translation element must be a descendant of a voxit-conversation element");
            return;
        }

        this.#buttonElement.addEventListener("click", this);
        for (const eventType of [
            "voxitstatechange",
            "voxitvotesubmitsuccess",
            "voxitstatementschange",
            "voxitrevisevote",
            "voxitconversationchange",
        ]) {
            this.#conversationElement.addEventListener(eventType, this);
        }
        this.#syncWithCurrentStatement();
    }

    disconnectedCallback() {
        this.#buttonElement.removeEventListener("click", this);
        if (this.#conversationElement !== null) {
            for (const eventType of [
                "voxitstatechange",
                "voxitvotesubmitsuccess",
                "voxitstatementschange",
                "voxitrevisevote",
                "voxitconversationchange",
            ]) {
                this.#conversationElement.removeEventListener(eventType, this);
            }
        }
        this.#abortController?.abort();
    }

    #label(attributeName, fallback) {
        return this.getAttribute(attributeName) ?? fallback;
    }

    #language() {
        return this.getAttribute("language") || document.documentElement.lang || navigator.language || "";
    }

    #translationLanguage() {
        const language = this.#language();
        const [primaryLanguage] = language.split(/[-_]/);
        return primaryLanguage || language;
    }

    #polisBaseURL() {
        return this.getAttribute("polis-base") || "";
    }

    #conversationID() {
        return this.#conversationElement?.client?.conversationID ??
            this.#conversationElement?.getAttribute("conversation-id") ??
            "";
    }

    #currentStatement() {
        return this.#conversationElement?.statement ?? null;
    }

    #statementElement() {
        return this.#conversationElement?.querySelector("voxit-statement") ?? null;
    }

    #cacheKey(statementID, language, polisBaseURL) {
        return `${polisBaseURL}\n${this.#conversationID()}\n${statementID}\n${language}`;
    }

    #currentCacheKey() {
        const statement = this.#currentStatement();
        if (statement === null || typeof statement.id === "undefined") {
            return "";
        }
        return this.#cacheKey(String(statement.id), this.#translationLanguage(), this.#polisBaseURL());
    }

    #withoutStatementIDs(statementID) {
        const statementIDs = this.#conversationElement?.client?.statements?.keys?.();
        if (typeof statementIDs === "undefined") {
            return [];
        }
        return Array.from(statementIDs, (id) => String(id)).filter((id) => id !== statementID);
    }

    #bestTranslation(payload, language) {
        const translations = Array.isArray(payload) ? payload : payload ? [payload] : [];
        const candidates = translations.filter((translation) => {
            return translation !== null &&
                typeof translation === "object" &&
                typeof translation.txt === "string" &&
                translation.txt !== "";
        });
        const normalizedLanguage = language.toLowerCase();
        const languagePrefix = normalizedLanguage.slice(0, 2);
        const matchingCandidates = candidates.filter((translation) => {
            return typeof translation.lang === "string" &&
                translation.lang.toLowerCase().startsWith(languagePrefix);
        });
        const sortedCandidates = (matchingCandidates.length > 0 ? matchingCandidates : candidates).slice();
        sortedCandidates.sort((a, b) => {
            const aLang = typeof a.lang === "string" ? a.lang.toLowerCase() : "";
            const bLang = typeof b.lang === "string" ? b.lang.toLowerCase() : "";
            if (aLang !== bLang) {
                if (aLang === normalizedLanguage) {
                    return -1;
                }
                if (bLang === normalizedLanguage) {
                    return 1;
                }
            }
            if (Number(a.src) > 0 && Number(b.src) <= 0) {
                return -1;
            }
            if (Number(b.src) > 0 && Number(a.src) <= 0) {
                return 1;
            }
            return 0;
        });
        return sortedCandidates[0] ?? null;
    }

    #translatedTextFromComment(payload, statementID, language) {
        if (payload === null || typeof payload !== "object" || String(payload.tid) !== String(statementID)) {
            return null;
        }
        if (Array.isArray(payload.translations)) {
            return this.#bestTranslation(payload.translations, language)?.txt ?? "";
        }
        if (payload.translatedText !== null && typeof payload.translatedText === "object") {
            return payload.translatedText[language] ??
                payload.translatedText[language.slice(0, 2)] ??
                "";
        }
        return "";
    }

    async #loadTranslation() {
        const statement = this.#currentStatement();
        const polisBaseURL = this.#polisBaseURL();
        const language = this.#translationLanguage();
        if (statement === null || typeof statement.id === "undefined" || !polisBaseURL || !language) {
            return;
        }

        const statementID = String(statement.id);
        const cacheKey = this.#cacheKey(statementID, language, polisBaseURL);
        if (this.#cache.has(cacheKey)) {
            this.#translationText = this.#cache.get(cacheKey);
            this.#render();
            return;
        }

        this.#abortController?.abort();
        const abortController = new AbortController();
        this.#abortController = abortController;
        this.#loading = true;
        this.#translationText = "";
        this.#render();

        try {
            const url = new URL("/api/v3/nextComment", polisBaseURL);
            url.searchParams.set("conversation_id", this.#conversationID());
            url.searchParams.set("lang", language);
            // NOTE: This is a workaround for the `nextComment` endpoint as the only endpoint to reliably return translations.
            // The endpoint randomizes the next comment but excluding other IDs allows us to retrieve a specific translation.
            const withoutStatementIDs = this.#withoutStatementIDs(statementID);
            if (withoutStatementIDs.length > 0) {
                url.searchParams.set("without", withoutStatementIDs.join(","));
            }

            const response = await fetch(url, {
                mode: "cors",
                credentials: "include",
                signal: abortController.signal,
            });
            if (!response.ok) {
                throw new Error(`Translation request failed with status ${response.status}`);
            }
            const translationText = this.#translatedTextFromComment(await response.json(), statementID, language);
            if (translationText === null) {
                if (this.#currentCacheKey() === cacheKey) {
                    this.#translationText = "";
                }
                return;
            }
            this.#cache.set(cacheKey, translationText);
            if (this.#currentCacheKey() === cacheKey) {
                this.#translationText = translationText;
            }
        } catch (error) {
            if (error instanceof DOMException && error.name === "AbortError") {
                return;
            }
            if (this.#currentCacheKey() === cacheKey) {
                this.#translationText = "";
            }
            console.error("Failed to load statement translation:", error);
        } finally {
            if (this.#abortController === abortController) {
                this.#abortController = null;
            }
            if (this.#currentCacheKey() === cacheKey) {
                this.#loading = false;
                this.#render();
            }
        }
    }

    #syncWithCurrentStatement() {
        const statement = this.#currentStatement();
        const language = this.#translationLanguage();
        const polisBaseURL = this.#polisBaseURL();
        if (statement === null || typeof statement.id === "undefined" || !polisBaseURL) {
            this.hidden = true;
            this.#lastStatementID = null;
            this.#translationText = "";
            this.#loading = false;
            this.#abortController?.abort();
            return;
        }

        this.hidden = false;
        const statementID = String(statement.id);
        if (
            statementID !== this.#lastStatementID ||
            language !== this.#lastLanguage ||
            polisBaseURL !== this.#lastPolisBaseURL
        ) {
            this.#lastStatementID = statementID;
            this.#lastLanguage = language;
            this.#lastPolisBaseURL = polisBaseURL;
            this.#translationText = "";
            this.#loading = false;
            this.#abortController?.abort();
            if (this.#enabled) {
                void this.#loadTranslation();
                return;
            }
        }
        this.#render();
    }

    #render() {
        this.#buttonElement.disabled = this.#loading;
        this.#buttonElement.setAttribute("aria-pressed", String(this.#enabled));
        this.#buttonElement.textContent = this.#enabled ?
            this.#label("hide-label", "Deactivate translation") :
            this.#label("show-label", "Activate third-party translation");

        const statement = this.#currentStatement();
        const statementElement = this.#statementElement();
        if (statementElement !== null && statement !== null) {
            statementElement.text = this.#enabled && this.#translationText ?
                this.#translationText :
                statement.text;
        }
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "click":
                this.#enabled = !this.#enabled;
                if (this.#enabled) {
                    void this.#loadTranslation();
                } else {
                    this.#loading = false;
                    this.#abortController?.abort();
                }
                this.#render();
                break;
            default:
                setTimeout(() => this.#syncWithCurrentStatement(), 0);
                break;
        }
    }
}
window.customElements.define("vrf-statement-translation", VoxitFrontendStatementTranslation);

class VoxitFrontendStackedLayer extends HTMLElement {
    static observedAttributes = ["selected"];

    constructor() {
        super();

        const template = document.querySelector("#vrf-stackedlayer-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendStackedLayerStyleSheet];
        shadow.appendChild(template.content.cloneNode(true));
    }

    attributeChangedCallback(name, oldValue, newValue) {
        console.debug(
            `Attribute ${name} has changed from ${oldValue} to ${newValue}`,
        );
        switch (name) {
            case "selected":
                if (oldValue !== newValue && newValue !== null) {
                    this.dispatchEvent(
                        new VoxitFrontendChangeEvent(
                            "voxitfrontendchange"
                        )
                    );
                }
                break;
        }
    }

    get selected() {
        return this.hasAttribute("selected");
    }

    set selected(value) {
        if (value !== this.selected) {
            if (value) {
                this.setAttribute("selected", "");
            } else {
                this.removeAttribute("selected");
            }
        }
    }
}
window.customElements.define("vrf-stackedlayer", VoxitFrontendStackedLayer);

class VoxitFrontendStacked extends HTMLElement {
    constructor() {
        super();

        const template = document.querySelector("#vrf-stacked-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendStackedStyleSheet];
        shadow.addEventListener("slotchange", this);
        shadow.appendChild(template.content.cloneNode(true));
    }

    connectedCallback() {
        this.shadowRoot.addEventListener("voxitfrontendchange", this);
    }

    disconnectedCallback() {
        this.shadowRoot.removeEventListener("voxitfrontendchange", this);
    }

    #updateLayers(selectedLayerElement = null) {
        selectedLayerElement ??= this.querySelector(":scope > vrf-stackedlayer[selected]");
        selectedLayerElement ??= this.querySelector(":scope > vrf-stackedlayer");
        for (const layerElement of this.querySelectorAll(":scope > vrf-stackedlayer")) {
            layerElement.selected = (layerElement === selectedLayerElement);
        }
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "slotchange":
                this.#updateLayers();
                break;
            case "voxitfrontendchange":
                if (ev.target.parentElement === this) {
                    this.#updateLayers(ev.target);
                }
                break;
        }
    }
}
window.customElements.define("vrf-stacked", VoxitFrontendStacked);

class VoxitFrontendTabPanel extends HTMLElement {
    #internals;

    constructor() {
        super();

        const template = document.querySelector("#vrf-tabpanel-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendTabPanelStyleSheet];
        shadow.appendChild(template.content.cloneNode(true));
        this.#internals = this.attachInternals();
    }

    connectedCallback() {
        this.#upgradeProperty("selected");
    }

    #upgradeProperty(property) {
        if (this.hasOwnProperty(property)) {
            let value = this[property];
            delete this[property];
            this[property] = value;
        }
    }

    get selected() {
        return this.#internals.states.has("selected");
    }

    set selected(value) {
        if (Boolean(value)) {
            this.#internals.states.add("selected");
        } else {
            this.#internals.states.delete("selected");
        }
    }
}
window.customElements.define("vrf-tabpanel", VoxitFrontendTabPanel);

class VoxitFrontendTab extends HTMLElement {
    static observedAttributes = ["selected"];

    constructor() {
        super();

        const template = document.querySelector("#vrf-tab-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendTabStyleSheet];
        shadow.appendChild(template.content.cloneNode(true));
    }

    connectedCallback() {
        for (const property of ["selected", "tabPanelTarget"]) {
            this.#upgradeProperty(property);
        }
        this.shadowRoot.addEventListener("click", this);
    }

    disconnectedCallback() {
        this.shadowRoot.removeEventListener("click", this);
    }

    attributeChangedCallback(name, oldValue, newValue) {
        console.debug(
            `Attribute ${name} has changed from ${oldValue} to ${newValue}`,
        );
        switch (name) {
            case "selected":
                if (oldValue !== newValue && newValue !== null) {
                    this.dispatchEvent(
                        new VoxitFrontendChangeEvent(
                            "voxitfrontendchange"
                        )
                    );
                }
                break;
        }
    }

    #upgradeProperty(property) {
        if (this.hasOwnProperty(property)) {
            let value = this[property];
            delete this[property];
            this[property] = value;
        }
    }

    get selected() {
        return this.hasAttribute("selected");
    }

    set selected(value) {
        value = Boolean(value);
        if (value !== this.selected) {
            if (value) {
                this.setAttribute("selected", "");
                this.tabIndex = 0;
            } else {
                this.removeAttribute("selected");
                this.tabIndex = -1;
            }
        }
    }

    get tabPanelTarget() {
        return this.getAttribute("tabpaneltarget");
    }

    set tabPanelTarget(value) {
        this.setAttribute("tabpaneltarget", value);
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "click":
                this.selected = true;
                break;
        }
    }
}
window.customElements.define("vrf-tab", VoxitFrontendTab);

class VoxitFrontendTabs extends HTMLElement {
    constructor() {
        super();

        const template = document.querySelector("#vrf-tabs-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendTabsStyleSheet];
        shadow.addEventListener("slotchange", this);
        shadow.appendChild(template.content.cloneNode(true));
    }

    connectedCallback() {
        this.addEventListener("keydown", this);
        this.shadowRoot.addEventListener("voxitfrontendchange", this);
    }

    disconnectedCallback() {
        this.shadowRoot.removeEventListener("voxitfrontendchange", this);
        this.removeEventListener("keydown", this);
    }

    #updateTabs(selectedTabElement = null) {
        selectedTabElement ??= this.querySelector(":scope > vrf-tab[selected]");
        selectedTabElement ??= this.querySelector(":scope > vrf-tab");
        for (const tabElement of this.querySelectorAll(":scope > vrf-tab")) {
            tabElement.selected = (tabElement === selectedTabElement);
        }
        const selectedTabPanelElement = this.getRootNode().getElementById(selectedTabElement?.tabPanelTarget);
        for (const tabPanelElement of this.querySelectorAll(":scope > vrf-tabpanel")) {
            tabPanelElement.selected = (tabPanelElement === selectedTabPanelElement);
        }
    }

    #selectNextTab() {
        const nextTabElement = this.querySelector(":scope > vrf-tab[selected] + vrf-tab:not([disabled], [inert], [hidden])");
        if (nextTabElement !== null) {
            nextTabElement.selected = true;
            nextTabElement.shadowRoot.querySelector("#tabbutton").focus();
        }
    }

    #selectPreviousTab() {
        const previousTabElement = this.querySelector(":scope > vrf-tab:not([disabled], [inert], [hidden]):has(+ vrf-tab[selected])");
        if (previousTabElement !== null) {
            previousTabElement.selected = true;
            previousTabElement.shadowRoot.querySelector("#tabbutton").focus();
        }
    }

    #selectFirstTab() {
        const firstTabElement = this.querySelector(":scope > vrf-tab:not([disabled], [inert], [hidden])");
        if (firstTabElement !== null) {
            firstTabElement.selected = true;
            firstTabElement.shadowRoot.querySelector("#tabbutton").focus();
        }
    }

    #selectLastTab() {
        const lastTabElement = this.querySelector(":scope > :nth-last-child(1 of vrf-tab:not([disabled], [inert], [hidden])");
        if (lastTabElement !== null) {
            lastTabElement.selected = true;
            lastTabElement.shadowRoot.querySelector("#tabbutton").focus();
        }
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "slotchange":
                this.#updateTabs();
                break;
            case "voxitfrontendchange":
                if (ev.target.parentElement === this) {
                    this.#updateTabs(ev.target);
                }
                break;
            case "keydown": {
                const writingMode = window.getComputedStyle(ev.target)["writing-mode"];
                switch (ev.key) {
                    case "ArrowLeft":
                        if (writingMode === "horizontal-tb") {
                            this.#selectPreviousTab();
                        }
                        break;
                    case "ArrowRight":
                        if (writingMode === "horizontal-tb") {
                            this.#selectNextTab();
                        }
                        break;
                    case "ArrowTop":
                        if (writingMode === "vertical-lr" ||
                                writingMode === "vertical-rl" ||
                                writingMode === "sideways-rl") {
                            this.#selectPreviousTab();
                        } else if (writingMode === "sideways-lr") {
                            this.#selectNextTab();
                        }
                        break;
                    case "ArrowDown":
                        if (writingMode === "vertical-lr" ||
                                writingMode === "vertical-rl" ||
                                writingMode === "sideways-rl") {
                            this.#selectNextTab();
                        } else if (writingMode === "sideways-lr") {
                            this.#selectPreviousTab();
                        }
                        break;
                    case "Home":
                        this.#selectFirstTab();
                        break;
                    case "End":
                        this.#selectLastTab();
                        break;
                }
                break;
            }
        }
    }
}
window.customElements.define("vrf-tabs", VoxitFrontendTabs);

class VoxitFrontendTooltip extends HTMLElement {
    static observedAttributes = ["value"];
    #tooltipElement;

    constructor() {
        super();

        const template = document.querySelector("#vrf-tooltip-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendTooltipStyleSheet];
        shadow.appendChild(template.content.cloneNode(true));
        this.#tooltipElement = shadow.querySelector("#tooltip");
    }

    connectedCallback() {
        const buttonElement = this.shadowRoot.querySelector("#anchor");
        buttonElement.addEventListener("mouseenter", this);
        buttonElement.addEventListener("mouseleave", this);
        if (!CSS.supports("position-anchor", "--anchor")) {
            window.addEventListener("resize", this);
            this.#tooltipElement.addEventListener("beforetoggle", this);
        }
    }

    disconnectedCallback() {
        const buttonElement = this.shadowRoot.querySelector("#anchor");
        buttonElement.removeEventListener("mouseleave", this);
        buttonElement.removeEventListener("mouseenter", this);
        if (!CSS.supports("position-anchor", "--anchor")) {
            window.removeEventListener("resize", this);
            this.#tooltipElement.removeEventListener("beforetoggle", this);
        }
    }

    #positionTooltipFallback() {
        const anchorElement = this.shadowRoot.querySelector("#anchor");
        const rect = anchorElement.getBoundingClientRect();
        const top = rect.top + rect.height + window.scrollY;
        const left = rect.left + window.scrollX;
        anchorElement.popoverTargetElement.style.setProperty("--anchored-inset", `${top}px auto auto ${left}px`);
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "beforetoggle": // FALLTHROUGH
            case "resize":
                this.#positionTooltipFallback()
                break;
            case "mouseenter":
                ev.target.popoverTargetElement.showPopover();
                break;
            case "mouseleave":
                ev.target.popoverTargetElement.hidePopover();
                break;
        }
    }
}
window.customElements.define("vrf-tooltip", VoxitFrontendTooltip);

class VoxitFrontendFlash extends HTMLElement {
    #messageElement;
    #flashTimer;

    constructor() {
        super();

        const template = document.querySelector("#vrf-flash-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendFlashStyleSheet];
        shadow.appendChild(template.content.cloneNode(true));
        this.#messageElement = shadow.querySelector("#message");
    }

    #onFlashTimer() {
        this.#messageElement.classList.remove("visible");
        this.#flashTimer = null;
    }

    show(duration = 2000) {
        this.#messageElement.classList.add("visible");
        clearTimeout(this.#flashTimer);
        this.#flashTimer = setTimeout(this.#onFlashTimer.bind(this), duration);
    }
}
window.customElements.define("vrf-flash", VoxitFrontendFlash);

class VoxitFrontendStatementCount extends HTMLElement {
    #conversationElement;

    constructor() {
        super();

        const template = document.querySelector("#vrf-statementcount-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendStatementCountStyleSheet];
        shadow.appendChild(template.content.cloneNode(true));
    }

    connectedCallback() {
        this.#conversationElement = this.closest("voxit-conversation");
        if (this.#conversationElement === null) {
            console.error("vrf-statementcount element must be a descendant of a voxit-conversation element");
            return;
        }
        this.#conversationElement.addEventListener("voxitstatechange", this);
        this.#conversationElement.addEventListener("voxitauthenticationsuccess", this);
        this.#conversationElement.addEventListener("voxitstatementschange", this);
        this.#conversationElement.addEventListener("voxitvotesubmitsuccess", this);
        this.#conversationElement.addEventListener("voxitrevisevote", this);
        this.#updateStatementCount();
        console.debug(`Associated with`, this.#conversationElement);
    }

    disconnectedCallback() {
        if (this.#conversationElement !== null) {
            this.#conversationElement.removeEventListener("voxitauthenticationsuccess", this);
            this.#conversationElement.removeEventListener("voxitstatechange", this);
            this.#conversationElement.removeEventListener("voxitstatementschange", this);
            this.#conversationElement.removeEventListener("voxitvotesubmitsuccess", this);
            this.#conversationElement.removeEventListener("voxitrevisevote", this);
            this.#conversationElement = null;
        }
    }

    #updateStatementCount() {
        const { current, total } = getStatementProgress(this.#conversationElement.client);
        const revisedCurrent = isRevisingVote(this.#conversationElement) ?
            Math.max(1, current - 1) :
            current;
        this.shadowRoot.querySelector("#statement-number").textContent = `${revisedCurrent}`;
        this.shadowRoot.querySelector("#statement-total").textContent = `${total}`;
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "voxitauthenticationsuccess": // FALLLTHROUGH
            case "voxitstatechange":           // FALLLTHROUGH
            case "voxitstatementschange":      // FALLLTHROUGH
            case "voxitrevisevote":           // FALLLTHROUGH
            case "voxitvotesubmitsuccess":
                this.#updateStatementCount();
                break;
        }
    }
}
window.customElements.define("vrf-statementcount", VoxitFrontendStatementCount);

class VoxitFrontendSplitProgress extends HTMLElement {
    #conversationElement;
    #fillElement;

    constructor() {
        super();

        const trackElement = document.createElement("div");
        trackElement.id = "track";
        this.#fillElement = document.createElement("div");
        this.#fillElement.id = "fill";
        trackElement.appendChild(this.#fillElement);
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendSplitProgressStyleSheet];
        shadow.appendChild(trackElement);
        this.setAttribute("aria-hidden", "true");
    }

    connectedCallback() {
        this.#conversationElement = this.closest("voxit-conversation");
        if (this.#conversationElement === null) {
            console.error("vrf-splitprogress element must be a descendant of a voxit-conversation element");
            return;
        }
        this.#conversationElement.addEventListener("voxitstatechange", this);
        this.#conversationElement.addEventListener("voxitauthenticationsuccess", this);
        this.#conversationElement.addEventListener("voxitstatementschange", this);
        this.#conversationElement.addEventListener("voxitvotesubmitsuccess", this);
        this.#conversationElement.addEventListener("voxitrevisevote", this);
        this.#updateProgress();
    }

    disconnectedCallback() {
        if (this.#conversationElement !== null) {
            this.#conversationElement.removeEventListener("voxitauthenticationsuccess", this);
            this.#conversationElement.removeEventListener("voxitstatechange", this);
            this.#conversationElement.removeEventListener("voxitstatementschange", this);
            this.#conversationElement.removeEventListener("voxitvotesubmitsuccess", this);
            this.#conversationElement.removeEventListener("voxitrevisevote", this);
            this.#conversationElement = null;
        }
    }

    #updateProgress() {
        const { current, total } = getStatementProgress(this.#conversationElement.client);
        const revisedCurrent = isRevisingVote(this.#conversationElement) ?
            Math.max(1, current - 1) :
            current;
        const half = total / 2;
        let width = 0;
        if (half > 0) {
            if (this.getAttribute("side") === "upper") {
                width = Math.max(0, Math.min(((revisedCurrent - half) / half) * 100, 100));
            } else {
                width = Math.max(0, Math.min((revisedCurrent / half) * 100, 100));
            }
        }
        this.#fillElement.style.inlineSize = `${width}%`;
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "voxitauthenticationsuccess": // FALLTHROUGH
            case "voxitstatechange": // FALLTHROUGH
            case "voxitstatementschange": // FALLTHROUGH
            case "voxitrevisevote": // FALLTHROUGH
            case "voxitvotesubmitsuccess":
                this.#updateProgress();
                break;
        }
    }
}
window.customElements.define("vrf-splitprogress", VoxitFrontendSplitProgress);

class VoxitFrontendResults extends HTMLElement {
    #conversationElement;

    constructor() {
        super();

        const template = document.querySelector("#vrf-results-template");
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendResultsStyleSheet];
        shadow.appendChild(template.content.cloneNode(true));
    }

    connectedCallback() {
        this.#conversationElement = this.closest("voxit-conversation");
        if (this.#conversationElement === null) {
            console.error("vrf-results element must be a descendant of a voxit-conversation element");
            return;
        }
        this.#conversationElement.addEventListener("voxitstatechange", this);
        this.#conversationElement.addEventListener("voxitresultschange", this);
        console.debug(`Associated with`, this.#conversationElement);
    }

    disconnectedCallback() {
        if (this.#conversationElement !== null) {
            this.#conversationElement.removeEventListener("voxitresultschange", this);
            this.#conversationElement.removeEventListener("voxitstatechange", this);
            this.#conversationElement = null;
        }
    }

    #createGroupResultNodes(results) {
        const resultNodes = [];
        for (const type of ["agree", "disagree"]) {
            for (const result of results[type]) {
                const valuePercent = Math.round(result.value * 100);
                const resultItemTemplate = this.shadowRoot.querySelector(`#result-${type}-template`);
                const clone = resultItemTemplate.content.cloneNode(true);
                const statementText = result.statementText.length > 1000 ?
                        result.statementText.slice(0, 1000) + "\u2026" :
                        result.statementText;
                clone.querySelector(".result-statement").textContent = statementText;
                clone.querySelector(".result-value-percent").textContent = `${valuePercent}`;
                clone.querySelector(".result-bar").value = valuePercent;
                resultNodes.push(clone);
            }
        }
        resultNodes.sort((a, b) => {
            const aValue = a.querySelector(".result-bar").value;
            const bValue = b.querySelector(".result-bar").value;
            if (aValue > bValue) {
                return -1;
            } else if (aValue < bValue) {
                return 1;
            }
            return 0;
        });
        return resultNodes;
    }

    #updateResults() {
        const majorityListElement = this.shadowRoot.querySelector("#majority .results-list");
        majorityListElement.replaceChildren(...this.#createGroupResultNodes(this.#conversationElement.client.results.majority));

        for (const [i, tabElement] of this.shadowRoot.querySelectorAll("vrf-tab:not([tabpaneltarget='majority'])").entries()) {
            const resultGroup = this.#conversationElement.client.results.groups[i];
            const tabPanelElement = this.shadowRoot.getElementById(tabElement.tabPanelTarget);
            const resultsListElement = tabPanelElement.querySelector(".results-list");
            if (typeof resultGroup !== "undefined") {
                resultsListElement.replaceChildren(...this.#createGroupResultNodes(resultGroup));
            } else {
                // FIXME show placeholder
                resultsListElement.replaceChildren();
            }
        }
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "voxitstatechange":
                if (ev.state === "loaded") {
                    this.#updateResults();
                }
                break;
            case "voxitresultschange":
                this.#updateResults();
                break;
        }
    }
}
window.customElements.define("vrf-results", VoxitFrontendResults);

class VoxitFrontendContextHelp extends HTMLElement {
    #bubbleElement;

    constructor() {
        super();

        const template = document.querySelector("#vrf-contexthelp-template");
        const clone = template.content.cloneNode(true)
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [voxitFrontendContextHelpStyleSheet];
        shadow.appendChild(clone);
        this.#bubbleElement = shadow.querySelector("#bubble");
    }

    connectedCallback() {
        if (!CSS.supports("position-anchor", "--anchor")) {
            window.addEventListener("resize", this);
            this.#bubbleElement.addEventListener("beforetoggle", this);
        }
    }

    disconnectedCallback() {
        if (!CSS.supports("position-anchor", "--anchor")) {
            window.removeEventListener("resize", this);
            this.#bubbleElement.removeEventListener("beforetoggle", this);
        }
    }

    #positionBubbleFallback() {
        const anchorElement = this.shadowRoot.querySelector("#help-button");
        const rect = anchorElement.getBoundingClientRect();
        const documentElement = document.documentElement;
        const right = documentElement.clientWidth - (rect.right + window.scrollX);
        const bottom = documentElement.clientHeight - (rect.top + window.scrollY);
        anchorElement.popoverTargetElement.style.setProperty("--anchored-inset", `auto ${right}px ${bottom}px auto`);
    }

    handleEvent(ev) {
        switch (ev.type) {
            case "beforetoggle": // FALLTHROUGH
            case "resize":
                this.#positionBubbleFallback()
                break;
        }
    }
}
window.customElements.define("vrf-contexthelp", VoxitFrontendContextHelp);

class VoxitFrontend {
    conversationElement = null;
    stckedElement = null;
    errorDialog = null;
    loginDialog = null;
    states = Object.freeze({
        INITIAL: "initial",
        CONVERSATION_DIALOG: "conversation-form",
        LOADING: "loading",
        INTRO: "introduction",
        VOTING: "voting",
        STATEMENT_EDITOR: "statement-editing",
        INACTIVE: "inactive",
        ERROR: "error",
    });
    events = Object.freeze({
        OPEN_CONVERSATION_DIALOG: "open-conversation-form",
        LOAD: "load",
        LOADED: "loaded",
        ERROR: "error",
        SET_INACTIVE: "set-inactive",
        OPEN_STATEMENT_EDITOR: "open-statement-editing",
        CLOSE_STATEMENT_EDITOR: "close-statement-editing",
        START_VOTING: "start-voting",
    });
    transitions = {
        [this.states.INITIAL]: {
            [this.events.OPEN_CONVERSATION_DIALOG]: this.states.CONVERSATION_DIALOG,
            [this.events.LOAD]: this.states.LOADING,
        },
        [this.states.LOADING]: {
            [this.events.LOADED]: this.states.INTRO,
            [this.events.SET_INACTIVE]: this.states.INACTIVE,
            [this.events.ERROR]: this.states.ERROR,
        },
        [this.states.INTRO]: {
            [this.events.SET_INACTIVE]: this.states.INACTIVE,
            [this.events.START_VOTING]: this.states.VOTING,
        },
        [this.states.VOTING]: {
            [this.events.SET_INACTIVE]: this.states.INACTIVE,
        },
        [this.states.STATEMENT_EDITOR]: {
            [this.events.SET_INACTIVE]: this.states.INACTIVE,
            [this.events.CLOSE_STATEMENT_EDITOR]: this.states.VOTING,
        },
        [this.states.INACTIVE]: {
        },
        [this.states.ERROR]: {
        },
    }
    state = this.states.INITIAL;

    constructor() {
        this.languageSelectElement = document.querySelector("#language-select");
        this.languageSelectElement?.addEventListener("change", this);
        const urlParams = new URLSearchParams(window.location.search);
        if (!urlParams.get("conversation_id")) {
            this.state = this.transitionState(this.state, this.events.OPEN_CONVERSATION_DIALOG);
        } else {
            this.state = this.transitionState(this.state, this.events.LOAD);
        }
        this.renderState();
    }

    showConversationForm() {
        const urlParams = new URLSearchParams(window.location.search);
        document.forms.conversation_form.elements.conversation_id.value = urlParams.get("conversation_id") ?? "";
        document.querySelector("#conversation-dialog").showModal();
    }

    loadConversation() {
        const urlParams = new URLSearchParams(window.location.search);
        const conversationID = urlParams.get("conversation_id");

        const conversationClone = document.querySelector("#conversation-template").content.cloneNode(true);

        this.loginDialog = conversationClone.querySelector("#login-dialog");
        this.loginDialog.addEventListener("close", ev => {
            if (this.conversationElement.client.authenticationRequired && !this.conversationElement.client.authenticated) {
                ev.target.showModal();
            }
        })
        this.errorDialog = conversationClone.querySelector("#error-dialog");
        this.errorDialog.addEventListener("close", ev => {
            ev.target.showModal();
        })

        this.conversationElement = conversationClone.querySelector("voxit-conversation");
        this.conversationElement.addEventListener("voxitstatechange", this);
        this.conversationElement.addEventListener("voxitauthenticationchange", this);
        this.conversationElement.addEventListener("voxitauthenticationsuccess", this);
        this.conversationElement.addEventListener("voxitauthenticationerror", this);
        this.conversationElement.addEventListener("voxitvotesubmiterror", this);
        this.conversationElement.addEventListener("voxitvotesubmitsuccess", this);
        this.conversationElement.addEventListener("voxitstatementsubmit", this);
        this.conversationElement.addEventListener("voxitstatementsubmitsuccess", this);
        this.conversationElement.addEventListener("voxitstatementsubmiterror", this);
        this.conversationElement.addEventListener("voxitconversationchange", this);
        this.conversationElement.addEventListener("voxitrevisevote", this);
        this.conversationElement.addEventListener("voxitresultschange", this);
        this.conversationElement.addEventListener("click", this);
        this.conversationElement.addEventListener("input", this);
        window.addEventListener("resize", this);
        this.conversationElement.setAttribute("conversation-id", conversationID);
        this.conversationElement._vrfPreviousStatement = null;

        document.querySelector("main").appendChild(conversationClone);
    }

    reviseVote() {
        const previousStatement = this.conversationElement?._vrfPreviousStatement;
        if (previousStatement === null || typeof previousStatement === "undefined") {
            return;
        }
        if (!canReviseVote(this.conversationElement)) {
            return;
        }

        this.conversationElement.statement = previousStatement;
        renderConversationStatement(this.conversationElement);
    }

    renderDescriptions() {
        const conversation = this.conversationElement?.client?.conversation;
        if (typeof conversation === "undefined") {
            return;
        }
        const descriptionNodes = createDescriptionNodes(
            conversation.descriptionDocument,
            conversation.description,
        );
        for (const descriptionElement of this.conversationElement.querySelectorAll("[data-voxit-description]")) {
            descriptionElement.replaceChildren(...descriptionNodes.map((node) => node.cloneNode(true)));
        }
    }

    updateDescriptionToggle() {
        const descriptionWrapperElement = this.conversationElement?.querySelector("#voting .description-wrapper");
        const descriptionElement = descriptionWrapperElement?.querySelector(".description");
        const descriptionToggleElement = descriptionWrapperElement?.querySelector(".description-toggle");

        if (
            typeof descriptionWrapperElement === "undefined"
            || descriptionWrapperElement === null
            || typeof descriptionElement === "undefined"
            || descriptionElement === null
            || typeof descriptionToggleElement === "undefined"
            || descriptionToggleElement === null
        ) {
            return;
        }

        const computedStyle = window.getComputedStyle(descriptionElement);
        const lineHeight = Number.parseFloat(computedStyle.lineHeight);
        const collapsedHeight = Number.isFinite(lineHeight) ? lineHeight * 5 : descriptionElement.clientHeight;
        const wasExpanded = descriptionWrapperElement.hasAttribute("expanded");
        descriptionWrapperElement.removeAttribute("expanded");
        descriptionToggleElement.hidden = true;
        descriptionElement.style.maxHeight = `${collapsedHeight}px`;
        window.requestAnimationFrame(() => {
            const expandedHeight = descriptionElement.scrollHeight;
            const isOverflowing = expandedHeight > collapsedHeight + 1;
            descriptionToggleElement.hidden = !isOverflowing;
            if (isOverflowing && wasExpanded) {
                descriptionWrapperElement.setAttribute("expanded", "");
            }
            if (isOverflowing) {
                if (descriptionWrapperElement.hasAttribute("expanded")) {
                    descriptionElement.style.maxHeight = `${expandedHeight}px`;
                } else {
                    descriptionElement.style.maxHeight = `${collapsedHeight}px`;
                }
            } else {
                descriptionElement.style.maxHeight = "none";
            }
            this.updateDescriptionToggleLabel(expandedHeight, collapsedHeight);
        });
    }

    updateDescriptionToggleLabel(expandedHeight = null, collapsedHeight = null) {
        const descriptionWrapperElement = this.conversationElement?.querySelector("#voting .description-wrapper");
        const descriptionElement = descriptionWrapperElement?.querySelector(".description");
        const descriptionToggleElement = descriptionWrapperElement?.querySelector(".description-toggle");
        const descriptionToggleLabelElement = descriptionToggleElement?.querySelector(".description-toggle-label");
        if (
            typeof descriptionWrapperElement === "undefined"
            || descriptionWrapperElement === null
            || typeof descriptionElement === "undefined"
            || descriptionElement === null
            || typeof descriptionToggleElement === "undefined"
            || descriptionToggleElement === null
            || typeof descriptionToggleLabelElement === "undefined"
            || descriptionToggleLabelElement === null
        ) {
            return;
        }

        const expanded = descriptionWrapperElement.hasAttribute("expanded");
        if (expandedHeight !== null && collapsedHeight !== null) {
            descriptionElement.style.maxHeight = expanded ?
                `${expandedHeight}px` :
                `${collapsedHeight}px`;
        }
        descriptionToggleLabelElement.textContent = expanded ?
            descriptionToggleElement.dataset.collapseLabel :
            descriptionToggleElement.dataset.expandLabel;
        descriptionToggleElement.setAttribute("aria-expanded", String(expanded));
    }

    updateResultsAvailable() {
        if (this.conversationElement.client.conversation.resultsAvailable) {
            this.conversationElement.classList.add("results-available");
        } else {
            this.conversationElement.classList.remove("results-available");
        }
    }

    updateStatementsAllowed() {
        if (this.conversationElement.client.conversation.statementsAllowed) {
            this.conversationElement.classList.add("statements-allowed");
        } else {
            this.conversationElement.classList.remove("statements-allowed");
        }
    }

    showStatementSubmittedFlash() {
        document.querySelector("#statement-submitted-flash")?.show();
    }

    statementInputElement() {
        return this.conversationElement?.querySelector("#statement") ?? null;
    }

    statementStatusElement() {
        return this.conversationElement?.querySelector(".statement-submit-status") ?? null;
    }

    setStatementStatus(status, message) {
        const statusElement = this.statementStatusElement();
        if (statusElement === null) {
            return;
        }
        statusElement.hidden = !message;
        if (!message) {
            delete statusElement.dataset.status;
            statusElement.textContent = "";
            return;
        }
        statusElement.dataset.status = status;
        statusElement.textContent = message;
    }

    clearStatementStatus() {
        this.setStatementStatus("", "");
    }

    statementStatusMessage(name, fallbackMessage = "") {
        const statusElement = this.statementStatusElement();
        if (statusElement === null) {
            return fallbackMessage;
        }
        const value = statusElement.dataset[name] ?? "";
        return value || fallbackMessage;
    }

    statementSubmitErrorMessage(error) {
        const detail = typeof error?.detail === "string" ? error.detail.toLowerCase() : "";
        const message = typeof error?.message === "string" ? error.message.toLowerCase() : "";
        if (
            error?.type === "tag:voxit.org,2024:api:errors:statement_exists" ||
            error?.constructor?.name === "StatementExistsError" ||
            message.includes("statement already exists") ||
            detail.includes("identical statement")
        ) {
            return this.statementStatusMessage("errorStatementExistsLabel");
        }
        switch (error?.constructor?.name) {
            case "AuthenticationRequiredError":
            case "SessionRequiredError":
                return this.statementStatusMessage("errorAuthRequiredLabel");
            case "ConversationInactiveError":
                return this.statementStatusMessage("errorInactiveLabel");
            case "StatementsNotAllowedError":
                return this.statementStatusMessage("errorNotAllowedLabel");
            default:
                return this.statementStatusMessage("errorGenericLabel");
        }
    }

    showIntro() {
        document.querySelector("title").textContent = this.conversationElement.client.conversation.topic;
        document.querySelector("#intro").selected = true;
    }

    showVoting() {
        document.querySelector("title").textContent = this.conversationElement.client.conversation.topic;
        document.querySelector("#voting").selected = true;
        this.updateDescriptionToggle();
        if (this.conversationElement.client.authenticationRequired && !this.conversationElement.client.authenticated) {
            this.loginDialog.showModal();
        }
    }

    showStatementEditing() {
        document.querySelector("title").textContent = this.conversationElement.client.conversation.topic;
        document.querySelector("#voting").selected = true;
        if (this.conversationElement.client.authenticationRequired && !this.conversationElement.client.authenticated) {
            this.loginDialog.showModal();
        }
    }

    showInactive() {
        document.querySelector("title").textContent = this.conversationElement.client.conversation.topic;
        document.querySelector("#inactive").selected = true;
    }

    showError() {
        const selectedLayerElement = document.querySelector("#views > vrf-stackedlayer[selected]");
        if (selectedLayerElement !== null) {
            selectedLayerElement.selected = false;
        }
        this.errorDialog.querySelector("#error-details").textContent = this.conversationElement.error;
        this.errorDialog.showModal();
    }

    renderState() {
        switch (this.state) {
            case this.states.CONVERSATION_DIALOG:
                this.showConversationForm();
                break;
            case this.states.LOADING:
                this.loadConversation();
                break;
            case this.states.INTRO:
                this.showIntro();
                break;
            case this.states.VOTING:
                this.showVoting();
                break;
            case this.states.STATEMENT_EDITOR:
                this.showStatementEditing();
                break;
            case this.states.INACTIVE:
                this.showInactive();
                break;
            case this.states.ERROR:
                this.showError();
                break;
        }
    }

    transitionState(state, ev) {
        if (typeof this.transitions[state][ev] === "undefined") {
            throw new Error(`Invalid state transition, state: ${this.state}, event: ${ev}`);
        }
        return this.transitions[state][ev];
    }

    redirectToLanguage(languageCode) {
        if (typeof this.languageSelectElement === "undefined" || this.languageSelectElement === null) {
            return;
        }
        const currentPath = window.location.pathname;
        const defaultLanguage = this.languageSelectElement.dataset.defaultLanguage ?? "";
        const configuredLanguages = Array.from(this.languageSelectElement.options, option => option.value);
        const trimmedPath = currentPath.replace(/^\/+/, "");
        const pathSegments = trimmedPath === "" ? [] : trimmedPath.split("/");
        const currentLanguage = pathSegments[0];
        const hasLanguagePrefix = configuredLanguages.includes(currentLanguage);
        const contentPath = hasLanguagePrefix ? pathSegments.slice(1).join("/") : trimmedPath;
        const basePath = contentPath === "" ? "/" : `/${contentPath}`;
        const targetPath = languageCode === defaultLanguage ?
            basePath :
            `/${languageCode}${basePath === "/" ? "/" : basePath}`;
        window.location.assign(`${targetPath}${window.location.search}${window.location.hash}`);
    }

    toggleDescription(descriptionWrapperElement) {
        if (descriptionWrapperElement === null) {
            return;
        }
        const descriptionElement = descriptionWrapperElement.querySelector(".description");
        descriptionWrapperElement.toggleAttribute("expanded");
        if (descriptionElement !== null) {
            const computedStyle = window.getComputedStyle(descriptionElement);
            const lineHeight = Number.parseFloat(computedStyle.lineHeight);
            const collapsedHeight = Number.isFinite(lineHeight) ? lineHeight * 5 : descriptionElement.clientHeight;
            const expandedHeight = descriptionElement.scrollHeight;
            this.updateDescriptionToggleLabel(expandedHeight, collapsedHeight);
        } else {
            this.updateDescriptionToggleLabel();
        }
    }

    handleEvent(ev) {
        console.debug("Event:", ev);
        switch (ev.type) {
            case "change":
                if (ev.target === this.languageSelectElement) {
                    this.redirectToLanguage(ev.target.value);
                }
                break;
            case "voxitstatechange":
                switch (ev.state) {
                    case "loaded":
                        if (this.conversationElement.client.conversation.isActive) {
                            this.state = this.transitionState(this.state, this.events.LOADED);
                            if (!document.querySelector("#intro")) {
                                this.state = this.transitionState(this.state, this.events.START_VOTING);
                            }
                            this.renderDescriptions();
                            this.renderState();
                            this.updateStatementsAllowed();
                        } else {
                            this.renderDescriptions();
                            this.state = this.transitionState(this.state, this.events.SET_INACTIVE);
                            this.renderState();
                        }
                        this.updateResultsAvailable();
                        break;
                    case "error":
                        this.state = this.transitionState(this.state, this.events.ERROR);
                        this.renderState();
                        break;
                }
                break;
            case "voxitauthenticationchange":
                if (ev.authenticationRequired && !ev.authenticated) {
                    this.loginDialog.showModal();
                }
                break;
            case "voxitauthenticationsuccess":
                this.loginDialog.close();
                break;
            case "voxitauthenticationerror":
                break;
            case "voxitvotesubmiterror":
                break;
            case "voxitvotesubmitsuccess":
                if (typeof ev.statementID !== "undefined") {
                    this.conversationElement._vrfPreviousStatement =
                        this.conversationElement.client.statements.get(Number(ev.statementID)) ?? null;
                }
                break;
            case "voxitstatementsubmit":
                this.setStatementStatus(
                    "pending",
                    this.statementStatusMessage("submittingLabel"),
                );
                break;
            case "voxitstatementsubmitsuccess":
                this.setStatementStatus(
                    "success",
                    this.statementStatusMessage("successLabel"),
                );
                this.showStatementSubmittedFlash();
                break;
            case "voxitstatementsubmiterror":
                this.setStatementStatus(
                    "error",
                    this.statementSubmitErrorMessage(ev.error),
                );
                break;
            case "voxitconversationchange":
                this.renderDescriptions();
                if (!ev.conversation.isActive) {
                    this.state = this.transitionState(this.state, this.events.SET_INACTIVE);
                    this.renderState();
                }
                this.updateStatementsAllowed();
                this.updateDescriptionToggle();
                break;
            case "voxitresultschange":
                this.updateResultsAvailable();
                break;
            case "voxitrevisevote":
                this.reviseVote();
                break;
            case "click":
                switch (ev.target.closest("button")?.dataset.action) {
                    case "start-voting":
                        this.state = this.transitionState(this.state, this.events.START_VOTING);
                        this.renderState();
                        break;
                    case "add-statement":
                    case "close-statement":
                        break;
                    case "open-statement-instructions":
                        this.conversationElement?.querySelector("#statement-instructions-dialog")?.showModal();
                        break;
                    case "close-statement-instructions":
                        this.conversationElement?.querySelector("#statement-instructions-dialog")?.close();
                        break;
                    case "toggle-description":
                        this.toggleDescription(ev.target.closest(".description-wrapper"));
                        break;
                    case "toggle-results":
                        break;
                }
                break;
            case "input":
                if (ev.target === this.statementInputElement()) {
                    this.clearStatementStatus();
                }
                break;
            case "resize":
                this.updateDescriptionToggle();
                break;
        }
    }
}

new VoxitFrontend();
